import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { TimePicker, type TimeValue } from '@/components/ui/time-picker'
import { useCreateReminder } from '@/models/userReminder'

const DEFAULT_TIME: TimeValue = { hours: 9, minutes: 0 }

function combineDateTime(date: Date, time: TimeValue): Date {
  const combined = new Date(date)
  combined.setHours(time.hours, time.minutes, 0, 0)
  return combined
}

export function NewReminderDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const createMutation = useCreateReminder()
  const [message, setMessage] = useState('')
  const [date, setDate] = useState<Date | undefined>(undefined)
  const [time, setTime] = useState<TimeValue>(DEFAULT_TIME)

  const reset = () => {
    setMessage('')
    setDate(undefined)
    setTime(DEFAULT_TIME)
  }

  const handleSave = () => {
    if (!message.trim() || !date) return
    const when = combineDateTime(date, time)
    createMutation.mutate(
      { message: message.trim(), reminderDate: when.toISOString() },
      {
        onSuccess: () => {
          reset()
          onOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New reminder</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="reminder-message">Message</Label>
            <Textarea
              id="reminder-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's the reminder?"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Date</Label>
            <DateInput value={date} onChange={setDate} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Time</Label>
            <TimePicker value={time} onChange={setTime} />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={handleSave}
            disabled={!message.trim() || !date || createMutation.isPending}
            className="text-md"
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
